package com.shadowings.gero.data.lessons.chapter16

import androidx.compose.ui.unit.dp
import com.shadowings.gero.data.lessons.DistractorPool
import com.shadowings.gero.data.lessons.POSSESSIVE_PRONOUN_IDS
import com.shadowings.gero.data.lessons.PossessivePhrase
import com.shadowings.gero.data.lessons.buildPossessiveFormChoice
import com.shadowings.gero.data.lessons.buildPossessivePhrase
import com.shadowings.gero.data.lessons.buildPossessiveWordSelection
import com.shadowings.gero.data.lessons.calculatePossessivePhrases
import com.shadowings.gero.data.lessons.inlineMeaning
import com.shadowings.gero.data.repository.DatabaseRepositoryImpl
import com.shadowings.gero.data.repository.PossessivePairs
import com.shadowings.gero.domain.model.Characters
import com.shadowings.gero.domain.model.ExplanationContent
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.IExplanation
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.Option
import com.shadowings.gero.domain.model.SingleSelectionContent
import com.shadowings.gero.domain.model.TrueFalseContent
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.model.Word
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_1
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_10
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_11
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_12
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_13
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_14
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_15
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_16
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_17
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_18
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_19
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_2
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_20
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_21
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_3
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_4
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_5
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_6
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_7
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_8
import gero.composeapp.generated.resources.exp_content_possessivo_suffisso_9
import gero.composeapp.generated.resources.exp_content_word_reading_and_meaning
import gero.composeapp.generated.resources.exp_title_possessivo_suffisso
import gero.composeapp.generated.resources.exp_title_word_name
import gero.composeapp.generated.resources.quiz_possessivo_suffisso_tf_1
import gero.composeapp.generated.resources.quiz_possessivo_suffisso_tf_2
import gero.composeapp.generated.resources.quiz_possessivo_suffisso_tf_3
import gero.composeapp.generated.resources.quiz_possessivo_suffisso_tf_4
import gero.composeapp.generated.resources.quiz_ripasso_quale_gero_significa
import kotlin.random.Random

fun getPossessivoSuffissoContent(words: List<Word>, languageCode: String): List<IExplanation> {
    // ib gets its own word slide, so that ANIMO unlocks in the compendium (ḥꜣty, CUORE, is from Chapter 12).
    val animo = words.firstOrNull { it.id == "ANIMO" }?.let { word ->
        ExplanationContent(
            title = Res.string.exp_title_word_name,
            titleArgs = listOf(word.getMeaning(languageCode)),
            content = Res.string.exp_content_word_reading_and_meaning,
            contentArgs = listOf(
                word.reading,
                word.inlineMeaning(languageCode),
                word.getNote(languageCode)
            ),
            face = Faces.Happy,
            character = Characters.Alyn,
            composable = { MdCWord(word.gardiner, rtl = false, cart = false, size = 96.dp) },
            wordId = word.id
        )
    }

    return listOfNotNull(
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_1,
            face = Faces.Happy,
            character = Characters.Nilo,
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_2,
            face = Faces.Doubt,
            character = Characters.Zeta,
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_3,
            face = Faces.Happy,
            character = Characters.Alyn,
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_4,
            face = Faces.Idle,
            character = Characters.Alyn,
            composable = mdcSlide("O1h2:Z1-Y3-A1"),
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_5,
            face = Faces.Idle,
            character = Characters.Zeta,
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_6,
            face = Faces.Idle,
            character = Characters.Alyn,
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_7,
            face = Faces.Idle,
            character = Characters.Alyn,
            composable = mdcSlide("O1h2:Z1-A1"),
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_8,
            face = Faces.Idle,
            character = Characters.Alyn,
            composable = mdcSlide("O1h2:Z1-V31"),
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_9,
            face = Faces.Idle,
            character = Characters.Alyn,
            composable = mdcSlide("O1h2:Z1-I9"),
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_10,
            face = Faces.Idle,
            character = Characters.Alyn,
            composable = mdcSlide("O1h2:Z1-N35:Z2"),
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_11,
            face = Faces.Surprised,
            character = Characters.Zeta,
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_12,
            face = Faces.Happy,
            character = Characters.Alyn,
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_13,
            face = Faces.Happy,
            character = Characters.Masko,
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_14,
            face = Faces.Idle,
            character = Characters.Alyn,
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_15,
            face = Faces.Idle,
            character = Characters.Alyn,
            composable = mdcSlide("F34-Z1-X1:F51"),
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_16,
            face = Faces.Idle,
            character = Characters.Alyn,
            composable = mdcSlide("F34h2:Z1"),
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_17,
            face = Faces.Serious,
            character = Characters.Zeta,
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_18,
            face = Faces.Idle,
            character = Characters.Alyn,
        ),
        animo,
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_19,
            face = Faces.Happy,
            character = Characters.Alyn,
            composable = mdcSlide("F34h2:Z1-I9"),
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_20,
            face = Faces.Happy,
            character = Characters.Nilo,
        ),
        ExplanationContent(
            title = Res.string.exp_title_possessivo_suffisso,
            content = Res.string.exp_content_possessivo_suffisso_21,
            face = Faces.Happy,
            character = Characters.Alyn,
        ),
    )
}

// The two hearts get their own question, so they stay out of the other ones.
private val heartIds = setOf("CUORE", "CUORI", "ANIMO")

// Owners for the direct-genitive distractor (pr sš, "the scribe's house").
private val ownerIds = listOf("SCRIBA", "PADRE", "MADRE", "FIGLIO", "SERVO", "SOLDATO", "DIO", "SOVRANO")

// 4 translations + 3 "pick the hieroglyphs", each on a different noun and spread over the persons,
// + 1 ib or ḥꜣty ("il suo animo" or "il suo cuore") + 2 true/false on the rule.
fun getPossessivoSuffissoQuizContent(
    words: List<Word>,
    languageCode: String,
    random: Random = Random,
    possessivePairs: List<PossessivePairs> = DatabaseRepositoryImpl.possessivePairs
): List<IQuizContent> {
    val known = DistractorPool.words(words).associateBy { it.id }
    val pronouns = POSSESSIVE_PRONOUN_IDS.mapNotNull { known[it] }
    val owners = ownerIds.mapNotNull { known[it] }
    val phrases = calculatePossessivePhrases(words, possessivePairs, languageCode)

    val picked = pickPerPerson(phrases.filter { it.noun.id !in heartIds }, 7, random)
    val nouns = phrases.map { it.noun }.distinctBy { it.id }
    val translations = picked.take(4).map { buildPossessiveWordSelection(it, nouns, languageCode, random) }
    val choices = picked.drop(4).take(3).map { buildPossessiveFormChoice(it, pronouns, owners, languageCode, random) }
    val heart = buildHeartChoice(known, pronouns, languageCode, random)
        ?: picked.getOrNull(7)?.let { buildPossessiveFormChoice(it, pronouns, owners, languageCode, random) }

    val theory = listOf(
        TrueFalseContent(Res.string.quiz_possessivo_suffisso_tf_1, false),
        TrueFalseContent(Res.string.quiz_possessivo_suffisso_tf_2, true),
        TrueFalseContent(Res.string.quiz_possessivo_suffisso_tf_3, true),
        TrueFalseContent(Res.string.quiz_possessivo_suffisso_tf_4, false),
    ).shuffled(random).take(2)

    return (translations + choices + listOfNotNull(heart) + theory).shuffled(random)
}

// One phrase per noun (drawn first, so nouns with more pronouns don't come out more often),
// cycling through the persons in random order so the same suffix doesn't come out every time.
// One extra is kept as a fallback for the heart question.
private fun pickPerPerson(phrases: List<PossessivePhrase>, count: Int, random: Random): List<PossessivePhrase> {
    val persons = POSSESSIVE_PRONOUN_IDS.shuffled(random)
    return phrases.groupBy { it.noun.id }.values
        .shuffled(random)
        .take(count + 1)
        .mapIndexed { index, sameNoun ->
            sameNoun.firstOrNull { it.pronoun.id == persons[index % persons.size] } ?: sameNoun.random(random)
        }
}

// "il suo animo" or "il suo cuore"? Options: the right one, the other heart, and both hearts with
// another suffix (one that doesn't translate the same way).
private fun buildHeartChoice(
    known: Map<String, Word>,
    pronouns: List<Word>,
    languageCode: String,
    random: Random
): SingleSelectionContent? {
    val animo = known["ANIMO"] ?: return null
    val cuore = known["CUORE"] ?: return null
    val (target, other) = listOf(animo, cuore).shuffled(random)
    val pronoun = pronouns.randomOrNull(random) ?: return null
    val right = buildPossessivePhrase(target, pronoun, languageCode)
    val otherPronoun = pronouns
        .filter { buildPossessivePhrase(target, it, languageCode).correctPhrase != right.correctPhrase }
        .randomOrNull(random) ?: return null

    val wrong = listOf(
        buildPossessivePhrase(other, pronoun, languageCode),
        buildPossessivePhrase(target, otherPronoun, languageCode),
        buildPossessivePhrase(other, otherPronoun, languageCode),
    ).map { it.hieroglyphs }

    return SingleSelectionContent(
        text = Res.string.quiz_ripasso_quale_gero_significa,
        textArgs = listOf(right.correctPhrase),
        detail = "",
        options = (listOf(Option(right.hieroglyphs, correct = true)) + wrong.map { Option(it, correct = false) })
            .shuffled(random)
    )
}
